/* eslint-disable @stylistic/max-len */
import { ReactNode, useEffect } from 'react';
import { useStore } from 'react-redux';
import { useTrackFunnelInfo } from 'core/analytics/hooks';
import { BASE_ROUTES } from 'core/common/constants';
import { useIsomorphicLayoutEffect, useRouter, useServices } from 'core/common/hooks';
import {
  ATTRS_ANALYTICS_FLOW_NAME,
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  ATTRS_ANALYTICS_FUNNEL_UTM_SECRET,
  SpanName,
} from 'core/common/observability/entities';
import { RootInitialState } from 'core/common/store';
import { cloneDeep } from 'core/common/utils/cloneDeep';
import { funnelFeatureDefaultValue } from 'core/feature-flags/config/funnelFeature';
import { useFeatureFlags } from 'core/feature-flags/hooks';
import { resolveFlowId } from 'core/feature-flags/utils';
import { Flow } from 'core/flow/entities';
import { FunnelConfig } from 'core/funnel/entities';
import i18n from 'core/i18n';
import { useAnalyticsIds } from 'core/scripts/hooks';
import { complianceCountries } from 'core/user/entities';
import { useUserLocation } from 'core/user/hooks';
import { useFlow } from '../hooks';
import { getConditionAttributes } from '../store/selectors';
import { FunnelContext } from './FunnelContext';

type FunnelProviderDeprecatedProps = {
  children: ReactNode;
  resources: FunnelConfig;
};

const isNormalFunnels = [
  'soulmate-sketch',
  'aura',
  'ex-compatibility',
  'ex-compatibility-np',
  'marriage-compatibility',
  'natal-chart',
  'palmistry-prediction',
  'witch-power',
  'past-life',
  'palmistry',
  'starseed',
  'birth-chart-calculator',
  'natal-chart-fb',
  'astrocartography-discovery',
  'ex-compatibility-applovin',
  'soulmate-sketch-applovin',
  'app-subscription-4-7d-trial-per-day',
  'soulmate-sketch-applovin-es',
  'feminine-archetypes',
  'moon-compatibility',
  'soul-connection',
  'telepathic-abilities',
];

/** @deprecated. Use FunnelProvider instead **/
export const FunnelProviderDeprecated = ({
  children,
  resources,
}: FunnelProviderDeprecatedProps) => {
  const { locale = 'en', navigate } = useRouter();
  const { fetchUserLocation, locale: location } = useUserLocation();
  const { updateFlow } = useFlow();

  useTrackFunnelInfo();

  const featureFlags = useFeatureFlags();

  const {
    analyticsService,
    errorMonitoringService,
    loggingService,
    funnelSettingsService,
    observabilitySystem,
  } = useServices();

  const store = useStore<RootInitialState>();

  const { flows, analytics, ...quizWithoutFlows } = resources;

  if (!featureFlags.getAttributes().funnel) {
    featureFlags.setAttributes({ funnel: resources.name });
  }

  const funnel = featureFlags.getFeatureValue(resources.name, funnelFeatureDefaultValue);
  const flowId = resolveFlowId(funnel, locale);

  const activeFlow: Flow | undefined = flows.find(({ name }) => name === flowId);
  const foundedDefaultFlow: Flow | undefined = flows.find(({ name }) => name === 'default_flow');

  const defaultFlow: Flow = foundedDefaultFlow ?? flows[0];

  const currentFlow = activeFlow ?? defaultFlow;

  if (featureFlags.getAttributes().flowName !== currentFlow.name) {
    featureFlags.setAttributes({ flowName: currentFlow.name });
  }

  const funnelConditions = getConditionAttributes(store.getState());

  const analyticsPixelIds = useAnalyticsIds({
    funnelAnalytics: analytics,
    funnelConditions,
  });

  useIsomorphicLayoutEffect(() => {
    fetchUserLocation();
    funnelSettingsService.setFunnelLocale(locale);
    funnelSettingsService.setProjectId(resources.project.id);
    funnelSettingsService.setFunnelId(resources.id);

    analyticsService.addUserAssociatedId(featureFlags.getSplitId());
  }, [analyticsService, funnelSettingsService, locale, resources.project.id, resources.id]);

  useEffect(() => {
    if (complianceCountries.includes(location) && !isNormalFunnels.includes(resources.name)) {
      navigate(BASE_ROUTES.HOME);
    }
  }, [location, resources.name, navigate]);

  useIsomorphicLayoutEffect(() => {
    const span = observabilitySystem.startSpan(SpanName.FLOW_RENDER);

    const utmCookies = featureFlags.getAttributes().utmCookies;

    if (!activeFlow && defaultFlow) {
      loggingService.warn(
        `Flow is missing. "${defaultFlow.name}" was used instead. Be sure to declare the neccessary flow in "quizResources" of the "${resources.name}" funnel.`,
      );

      const funnelName = defaultFlow.attributes.overriddenFunnelName || resources.name;

      span.setAttributes({
        [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
        [ATTRS_ANALYTICS_FLOW_NAME]: defaultFlow.name,
        [ATTRS_ANALYTICS_FUNNEL_LOCALE]: locale,
        [ATTRS_ANALYTICS_FUNNEL_UTM_SECRET]: utmCookies,
      });

      funnelSettingsService.setFunnelName(funnelName);
      funnelSettingsService.setFlowId(defaultFlow.id);
      funnelSettingsService.setFlowName(defaultFlow.name);

      errorMonitoringService.addCustomTags({
        funnel_name: funnelName,
        flow_name: defaultFlow.name,
        funnel_locale: locale,
      });

      analyticsService.funnelOpened({
        funnel: funnelName,
        flow: defaultFlow.name,
        language: defaultFlow.language,
      });

      i18n.changeLanguage(defaultFlow.language);
      updateFlow(defaultFlow);

      span.succeed();
    }

    if (activeFlow) {
      const funnelName = activeFlow.attributes.overriddenFunnelName || resources.name;

      span.setAttributes({
        [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
        [ATTRS_ANALYTICS_FLOW_NAME]: activeFlow.name,
        [ATTRS_ANALYTICS_FUNNEL_LOCALE]: locale,
        [ATTRS_ANALYTICS_FUNNEL_UTM_SECRET]: utmCookies,
      });

      funnelSettingsService.setFunnelName(funnelName);
      funnelSettingsService.setFlowId(activeFlow.id);
      funnelSettingsService.setFlowName(activeFlow.name);

      errorMonitoringService.addCustomTags({
        funnel_name: funnelName,
        flow_name: activeFlow.name,
        funnel_locale: locale,
      });

      analyticsService.funnelOpened({
        funnel: funnelName,
        flow: activeFlow.name,
        language: activeFlow.language,
      });

      i18n.changeLanguage(activeFlow.language);
      updateFlow(activeFlow);

      span.succeed();
    }
  }, [activeFlow, errorMonitoringService, funnelSettingsService]);

  if (!activeFlow && defaultFlow.translations) {
    i18n.addResourceBundle(
      defaultFlow.language,
      'translation',
      // in cause when defaultFlow.translations object stored in redux
      // all objects have been frozen by immer https://immerjs.github.io/immer/freezing/
      // addResourceBundle method can't work properly with frozen object
      cloneDeep(defaultFlow.translations),
      true,
      true,
    );
  }

  if (!activeFlow) {
    return (
      <FunnelContext.Provider
        value={{
          ...quizWithoutFlows,
          flow: defaultFlow,
          analyticsIds: {
            facebook: analyticsPixelIds.facebook.combined,
            tikTok: analyticsPixelIds.tikTok.combined,
            snapchat: analyticsPixelIds.snapchat.combined,
          },
        }}
      >
        {children}
      </FunnelContext.Provider>
    );
  }

  if (activeFlow && activeFlow.translations) {
    i18n.addResourceBundle(
      activeFlow.language,
      'translation',
      // in cause when activeFlow.translations object stored in redux
      // all objects have been frozen by immer https://immerjs.github.io/immer/freezing/
      // addResourceBundle method can't work properly with frozen object
      cloneDeep(activeFlow.translations),
      true,
      true,
    );
  }

  return (
    <FunnelContext.Provider
      value={{
        ...quizWithoutFlows,
        flow: activeFlow,
        analyticsIds: {
          facebook: analyticsPixelIds.facebook.combined,
          tikTok: analyticsPixelIds.tikTok.combined,
          snapchat: analyticsPixelIds.snapchat.combined,
        },
      }}
    >
      {children}
    </FunnelContext.Provider>
  );
};
